import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn, Globe } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { languageNames } from "../i18n/translations";

const SAG_LOGO = "/sag-logo.png";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  // Check for redirect after login (e.g., from invoice deep-link)
  const redirectTo = new URLSearchParams(location.search).get("redirect") || location.state?.from?.pathname;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name}!`);
      
      // Redirect to the original page if coming from a deep-link
      if (redirectTo) {
        navigate(redirectTo);
      } else {
        const dashboardRoutes = {
          admin: "/admin",
          consultant: "/consultant",
          field_agent: "/field-agent",
          client: "/client",
        };
        navigate(dashboardRoutes[user.role] || "/client");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Language Switcher - Top Right */}
      <div className="absolute top-4 right-4 z-50">
        <div className="flex items-center gap-1 bg-white/90 backdrop-blur rounded-lg shadow-sm border border-slate-200 px-1">
          {Object.entries(languageNames).map(([code, name]) => (
            <button
              key={code}
              onClick={() => setLanguage(code)}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                language === code ? "bg-sag-navy text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`login-lang-${code}`}
            >
              {code.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 gradient-brand items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-sag-blue rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 text-center text-white max-w-md">
          <img src={SAG_LOGO} alt="SAG Logo" className="w-48 h-48 mx-auto mb-8 object-contain" />
          <h1 className="font-heading text-4xl font-bold mb-4">Helpdesk Portal</h1>
          <p className="text-lg text-white/80">
            Streamlined service management connecting clients with expert consultants and field agents.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md shadow-floating border-0">
          <CardHeader className="space-y-1 text-center pb-2">
            <img src={SAG_LOGO} alt="SAG Logo" className="w-20 h-20 mx-auto lg:hidden mb-4 object-contain" />
            <CardTitle className="font-heading text-2xl font-bold text-sag-navy">{t("auth.loginTitle")}</CardTitle>
            <CardDescription className="text-slate-500">
              {t("auth.loginSubtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">{t("auth.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 border-slate-200 focus:border-sag-navy focus:ring-sag-navy/20"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">{t("auth.password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 pr-10 border-slate-200 focus:border-sag-navy focus:ring-sag-navy/20"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm text-sag-blue hover:text-sag-navy font-medium" data-testid="forgot-password-link">
                  Forgot Password?
                </Link>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-sag-navy hover:bg-sag-navy-800 text-white font-semibold"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                    {t("auth.signingIn")}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="h-4 w-4" />
                    {t("auth.login")}
                  </span>
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                {t("auth.noAccount")}{" "}
                <Link to="/register" className="text-sag-blue hover:text-sag-navy font-medium">
                  {t("auth.register")}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
